import { useState, useCallback, useEffect } from "react";
import { useBluelagoon } from "@bluelagoon";
import { useTranslation } from "react-i18next";
import { Badge } from "@shadcn-in-nx/ui/badge";
import { Button } from "@shadcn-in-nx/ui/button";
import { Loader2, Power, PowerOff, Trash2 } from "lucide-react";
import CompanyPatternRow from "./CompanyPatternRow";

export default function CompanyPatternsTab() {
    const { t } = useTranslation();
    const [patterns, setPatterns] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);

    const { call: fetchPatterns, isLoading } = useBluelagoon({
        method: "getCompanyPatterns",
    });
    const { call: updatePattern } = useBluelagoon({
        method: "updateCompanyPattern",
    });

    const load = useCallback(async () => {
        const res = await fetchPatterns({ page, limit: 20 });
        if (res?.data?.patterns) {
            setPatterns(res.data.patterns);
            setTotal(res.data.total);
        }
    }, [fetchPatterns, page]);

    useEffect(() => { load(); }, [load]);

    const handleAction = useCallback(async (patternId, action) => {
        await updatePattern({ patternId, action });
        load();
    }, [updatePattern, load]);

    if (isLoading && !patterns.length) {
        return (
            <div className="flex justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
        );
    }

    return (
        <div className="space-y-3 mt-4">
            <p className="text-sm text-muted-foreground">
                {t("admin.intelligence.companyPatterns.description")}
            </p>
            {patterns.map((p) => (
                <CompanyPatternRow key={p.id} pattern={p} onAction={handleAction} />
            ))}
            {patterns.length === 0 && (
                <p className="text-center text-muted-foreground py-8">
                    {t("admin.intelligence.companyPatterns.noPatterns")}
                </p>
            )}
            {total > 20 && (
                <div className="flex justify-center gap-2 pt-4">
                    <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                        {t("common.previous")}
                    </Button>
                    <span className="text-sm text-muted-foreground self-center">
                        {page} / {Math.ceil(total / 20)}
                    </span>
                    <Button size="sm" variant="outline" disabled={page >= Math.ceil(total / 20)} onClick={() => setPage(page + 1)}>
                        {t("common.next")}
                    </Button>
                </div>
            )}
        </div>
    );
}
